import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateCrmData } from '../../../../../store/deals/deals.actions';
import { RootState } from '../../../../../store/store';
import { ConnectCrm } from '../../../../Apps/components/connectCrmModal/ConnectCrmModal';
import editIcon from '../../../assets/edit-icon.svg';
import { CrmSkeleton } from '../skeletons/CrmSkeleton';

type InputProps = {
  value?: string | number;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  isComingSoon?: boolean;
  isEditIcon?: boolean;
};

const CrmInput: React.FC<InputProps> = ({
  value,
  placeholder,
  onChange,
  onClick,
  onKeyPress,
  disabled = false,
  isComingSoon = false,
  isEditIcon = false,
}): JSX.Element => {
  return (
    <div className="crm-input" onClick={onClick} onKeyDown={onKeyPress}>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type="text"
        disabled={disabled}
        className="crm-input__control"
      />
      {isComingSoon ? (
        <span className="crm-input__coming-soon">Coming soon</span>
      ) : null}
      {isEditIcon ? (
        <img src={editIcon} className="crm-input__edit-icon" alt="" />
      ) : null}
    </div>
  );
};

type Props = {
  isLoading: boolean;
};

export const CrmData: React.FC<Props> = ({ isLoading }): JSX.Element => {
  const user = useSelector((state: RootState) => state.auth.user);
  const details = useSelector(
    (state: RootState) => state.deals.detailedAnalysis?.deal
  );

  const dispatch = useDispatch();

  const [connectCrmOpen, setConnectCrmOpen] = useState(false);

  const [crmModalOpen, setCrmModalOpen] = useState(false);

  const [name, setName] = useState<string | number>('');
  const [stage, setStage] = useState<string | number>('');
  const [value, setValue] = useState<string | number>('');

  const [active, setActive] = useState<number>(0);

  const dealNameRef = useRef<HTMLDivElement>(null);
  const crmStageRef = useRef<HTMLDivElement>(null);
  const dealValueRef = useRef<HTMLDivElement>(null);

  const { pathname } = useLocation();

  const isInDetailedView =
    pathname.includes('example') && pathname.includes('threads');

  useEffect(() => {
    if (details) {
      const { dealName, crmStage, dealValue } = details;
      if (dealName && dealName.toString().length) {
        setName(dealName);
      }
      if (crmStage && crmStage.toString().length) {
        setStage(crmStage);
      }
      if (dealValue && dealValue.toString().length) {
        setValue(dealValue);
      }
    }
  }, [details]);

  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;

    const isNameActiveTarget = dealNameRef.current?.contains(target);
    const isStageActiveTarget = dealNameRef.current?.contains(target);
    const isValueActiveTarget = dealNameRef.current?.contains(target);

    if (user?.userId && user?.orgId) {
      const nameCheck = active === 1 && name !== details?.dealName;
      const stageCheck = active === 2 && stage !== details?.crmStage;
      const valueCheck = active === 3 && value !== details?.dealValue;

      const { orgId, userId } = user;
      const threadId = details?.platformThreadId || '';

      if (!isNameActiveTarget && nameCheck) {
        dispatch(
          updateCrmData(orgId, userId, threadId, { dealName: `${name}` })
        );
        setActive(0);
      } else if (!isStageActiveTarget && stageCheck) {
        dispatch(
          updateCrmData(orgId, userId, threadId, { crmStage: `${stage}` })
        );
        setActive(0);
      } else if (!isValueActiveTarget && valueCheck) {
        dispatch(
          updateCrmData(orgId, userId, threadId, { dealValue: `${value}` })
        );
        setActive(0);
      } else if (
        (isNameActiveTarget || !isStageActiveTarget || !isValueActiveTarget) &&
        active !== 0
      ) {
        setActive(0);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () =>
      document.removeEventListener('click', handleClickOutside, true);
  });

  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.currentTarget.value);
    if (active !== 1) {
      setActive(1);
    }
  };

  const onStageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStage(event.currentTarget.value);
    if (active !== 2) {
      setActive(2);
    }
  };

  const onValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.currentTarget.value);
    if (active !== 3) {
      setActive(3);
    }
  };

  // eslint-disable-next-line
  const keyboardKeyPress = (event: any) => {
    if (event.key === 'Enter' && user?.userId && user?.orgId) {
      const nameCheck = active === 1 && name !== details?.dealName;
      const stageCheck = active === 2 && stage !== details?.crmStage;
      const valueCheck = active === 3 && value !== details?.dealValue;

      const { orgId, userId } = user;
      const threadId = details?.platformThreadId || '';

      if (nameCheck) {
        setActive(0);
        dispatch(
          updateCrmData(orgId, userId, threadId, { dealName: `${name}` })
        );
      } else if (stageCheck) {
        setActive(0);
        dispatch(
          updateCrmData(orgId, userId, threadId, { crmStage: `${stage}` })
        );
      } else if (valueCheck) {
        setActive(0);
        dispatch(
          updateCrmData(orgId, userId, threadId, { dealValue: `${value}` })
        );
      }
    } else if (event.key === 'Escape') {
      const nameCancelCheck = active === 1 && name !== details?.dealName;
      const stageCancelCheck = active === 2 && stage !== details?.crmStage;
      const valueCancelCheck = active === 3 && value !== details?.dealValue;

      if (nameCancelCheck && typeof details?.dealName === 'string') {
        setName(details.dealName);
        setActive(0);
      } else if (stageCancelCheck && typeof details?.crmStage === 'string') {
        setStage(details.crmStage);
        setActive(0);
      } else if (valueCancelCheck && typeof details?.dealValue === 'string') {
        setValue(details.dealValue);
        setActive(0);
      }
    }
  };

  const toggleConnectCrm = () => {
    setConnectCrmOpen(!connectCrmOpen);
  };

  const clickContactUs = () => {
    setCrmModalOpen(!crmModalOpen);
  };

  const conditionalBody = connectCrmOpen ? (
    <div className="crm__contact">
      <div className="crm__contact-message">
        <div className="crm__contact-message-link" onClick={clickContactUs}>
          Contact us
        </div>{' '}
        for more information about CRM integration
      </div>
    </div>
  ) : (
    <>
      <div ref={dealNameRef}>
        <CrmInput
          placeholder="Deal name"
          isEditIcon={active !== 1}
          value={name}
          onClick={() => setActive(1)}
          onChange={onNameChange}
          onKeyPress={keyboardKeyPress}
        />
      </div>
      <div ref={crmStageRef}>
        <CrmInput
          placeholder="CRM stage"
          isEditIcon={active !== 2}
          value={stage}
          onClick={() => setActive(2)}
          onChange={onStageChange}
          onKeyPress={keyboardKeyPress}
        />
      </div>
      <div ref={dealValueRef}>
        <CrmInput
          placeholder="Deal value"
          isEditIcon={active !== 3}
          value={value}
          onClick={() => setActive(3)}
          onChange={onValueChange}
          onKeyPress={keyboardKeyPress}
        />
      </div>
      <CrmInput placeholder="Buying zone" disabled isComingSoon />
    </>
  );

  return (
    <CrmSkeleton isLoading={isLoading}>
      <div
        className={
          connectCrmOpen
            ? 'crm-background-contact'
            : `${isInDetailedView ? 'disabled' : ''} crm`
        }
      >
        <div className="crm__header">
          <span className="crm__header-label">CRM Data</span>{' '}
          <button
            className={
              connectCrmOpen ? 'active crm__header-btn' : 'crm__header-btn'
            }
            onClick={toggleConnectCrm}
          >
            Connect CRM
          </button>
        </div>
        {conditionalBody}
        <ConnectCrm open={crmModalOpen} onClose={clickContactUs} />
      </div>
    </CrmSkeleton>
  );
};
